import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, LayersControl, useMapEvents, useMap, Pane, GeoJSON } from 'react-leaflet';
import { CircularProgress, Typography } from '@mui/material';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Import optimized child components
import PlaceLayer from '../PlaceLayer/PlaceLayer';
import DropPointLayer from '../DropPointLayer/DropPointLayer';
import RiskAssessmentOverlay from '../RiskAssessmentOverlay/RiskAssessmentOverlay';
import MapEvents from '../MapEvents/MapEvents';
import Obstacles from '../../../Restaurant/components/Obstacles';
import { geofenceColor } from 'utils/ui';

// Import types
import { Place, DropPoint } from '@m4a3/api-client';
import { RiskAssessment } from '../../utils/riskUtils';
import type { ClickMode } from '../../models/click-mode';
import type { Obstacle } from '../../../../models/obstacle.interface';

interface MapViewProps {
  mapCenter: [number, number];
  mapZoom: number;
  places: Place[];
  dropPoints: DropPoint[];
  obstacles?: Obstacle[];
  geofences?: any[]; // legacy geofence shape from redux (id, geoJson, active, ...)
  selectedPlaceId: string | null;
  selectedDropPointId: string | null;
  preferredDropPointId?: string | null;
  placeStatuses: Map<string, { enabled: boolean; name?: string }>;
  placesUpdateKey: number;
  clickMode: ClickMode;
  loading: boolean;
  
  // New pin state
  newPinLocation: L.LatLng | null;
  riskAssessment: RiskAssessment | null;
  isLoadingRiskAssessment: boolean;
  
  // Event handlers
  onPlaceClick: (place: Place) => void;
  onDropPointClick: (dropPoint: DropPoint) => void;
  onMapClick: (coordinates: L.LatLng) => void;
  onMapMove: (center: L.LatLng, zoom: number) => void;
  onZoom: (center: L.LatLng, zoom: number) => void;
  onPinMove: (newPosition: L.LatLng) => void;
}

// Component to update map view when center/zoom props change
const MapController = ({
  center,
  zoom,
}: {
  center: [number, number];
  zoom: number;
}): JSX.Element => {
  const map = useMap();
  
  useEffect(() => {
    // Update map view when center or zoom changes
    map.setView(center, zoom);
  }, [map, center, zoom]);

  return null;
};

// Viewport tracking component
const ViewportTracker = ({
  onViewportChange,
}: {
  onViewportChange: (bounds: L.LatLngBounds) => void;
}): JSX.Element => {
  const map = useMap();
  const lastBounds = useRef<L.LatLngBounds | null>(null);

  useEffect(() => {
    const updateViewport = () => {
      const bounds = map.getBounds();
      
      // Only trigger if bounds changed significantly
      if (!lastBounds.current || !lastBounds.current.equals(bounds)) {
        lastBounds.current = bounds;
        onViewportChange(bounds);
      }
    };

    // Initial viewport
    updateViewport();

    // Listen for viewport changes
    map.on('moveend', updateViewport);
    map.on('zoomend', updateViewport);

    return () => {
      map.off('moveend', updateViewport);
      map.off('zoomend', updateViewport);
    };
  }, [map, onViewportChange]);

  return null;
};

const MapView = React.memo(({
  mapCenter,
  mapZoom,
  places,
  dropPoints,
  selectedPlaceId,
  selectedDropPointId,
  preferredDropPointId,
  placeStatuses,
  placesUpdateKey,
  clickMode,
  loading,
  newPinLocation,
  riskAssessment,
  isLoadingRiskAssessment,
  onPlaceClick,
  onDropPointClick,
  onMapClick,
  onMapMove,
  onZoom,
  onPinMove,
  obstacles,
  geofences,
}: MapViewProps): JSX.Element => {
  const [viewportBounds, setViewportBounds] = useState<L.LatLngBounds | undefined>();

  // Handle viewport changes with throttling
  const handleViewportChange = useCallback((bounds: L.LatLngBounds) => {
    setViewportBounds(bounds);
  }, []);

  // Memoize expensive calculations
  const visiblePlacesCount = React.useMemo(() => {
    if (!viewportBounds) return places.length;
    
    return places.filter(place => {
      if (place.centroid) {
        return viewportBounds.contains([place.centroid.latitude, place.centroid.longitude]);
      }
      return true; // Include if we can't determine position
    }).length;
  }, [places, viewportBounds]);

  const visibleDropPointsCount = React.useMemo(() => {
    if (!viewportBounds) return dropPoints.length;
    
    return dropPoints.filter(dp => 
      viewportBounds.contains([dp.latitude, dp.longitude])
    ).length;
  }, [dropPoints, viewportBounds]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        // Performance settings
        preferCanvas={true}
      >
        {/* Map controller to handle center/zoom changes */}
        <MapController center={mapCenter} zoom={mapZoom} />
        
        {/* Viewport tracker for optimization */}
        <ViewportTracker onViewportChange={handleViewportChange} />

        {/* Base Map Layers */}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Manna v1">
            <TileLayer
              attribution="Manna-v1"
              url="https://tiles.manna.aero/{z}/{x}/{y}.png"
              maxZoom={24}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Manna v2">
            <TileLayer
              attribution="Manna-v2"
              url="https://tiles-dev.manna.aero/{z}/{x}/{y}.png"
              maxZoom={24}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={22}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        
        {/* Optimized Places Layer with viewport culling */}
        <PlaceLayer
          places={places}
          selectedPlaceId={selectedPlaceId}
          placeStatuses={placeStatuses}
          placesUpdateKey={placesUpdateKey}
          clickMode={clickMode}
          onPlaceClick={onPlaceClick}
        />
        
        {/* Obstacles */}
        <Pane name="Obstacles" style={{ zIndex: 10005 }}>
          <Obstacles obstacles={obstacles as any} />
        </Pane>

        {/* Geofences */}
        <Pane name="Geofences" style={{ zIndex: 10004, pointerEvents: 'none' }}>
          {geofences
            ?.filter(g => g?.active === true && g?.geoJson)
            ?.map(g => (
              <GeoJSON
                key={g.id}
                data={g.geoJson}
                style={{ fill: false, color: geofenceColor(g.id) }}
                interactive={false}
              />
            ))}
        </Pane>
        
        {/* Optimized Drop Points Layer with spatial indexing */}
        <DropPointLayer
          dropPoints={dropPoints}
          selectedDropPointId={selectedDropPointId}
          preferredDropPointId={preferredDropPointId}
          onDropPointClick={onDropPointClick}
          viewportBounds={viewportBounds}
        />

        {/* Risk Assessment Overlay */}
        <RiskAssessmentOverlay
          selectedDropPointId={selectedDropPointId}
          dropPoints={dropPoints}
          newPinLocation={newPinLocation}
          riskAssessment={riskAssessment}
          isLoadingRiskAssessment={isLoadingRiskAssessment}
          clickMode={clickMode}
          onPinMove={onPinMove}
        />
        
        {/* Map Events Handler */}
        <MapEvents
          onMapClick={onMapClick}
          onMapMove={onMapMove}
          onZoom={onZoom}
          clickMode={clickMode}
        />
      </MapContainer>
      
      {/* Performance-aware loading overlay */}
      {loading && (
        <div 
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            zIndex: 1000,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Loading...</Typography>
        </div>
      )}

      {/* Performance debug info (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div 
          style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '8px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 1000,
          }}
        >
          <div>Places: {visiblePlacesCount}/{places.length}</div>
          <div>Drop Points: {visibleDropPointsCount}/{dropPoints.length}</div>
          <div>Zoom: {mapZoom}</div>
        </div>
      )}
    </div>
  );
});

MapView.displayName = 'MapView';

export default MapView;
